import { AccountStanding, Address, AddressInput, Order, OrderStatus, Shipping, ShippingChargeType } from 'API';
import { getOriginFacilityMapping } from 'configurations/OriginFacilityConfiguration';
import { round } from 'lodash';
import { InvoiceAccount } from 'shared/api/invoice.api';
import { CancelActivity } from 'shared/constants/case-detail.constants';
import { INVALID_INVOICE_STATUSES } from 'shared/constants/invoice.constants';
import { ErrorMessage, ShippingService } from 'shared/enums';
import { AnalyticsEventName } from 'shared/enums/analytics';
import {
  CannotInvoiceCallCenterError,
  CannotInvoiceCancelDiscardError,
  CannotInvoiceCreditHoldError,
  CannotInvoiceDiscountAmountError,
  CannotInvoiceMissingAlloyError,
  CannotInvoicePatientInfoError,
  CannotInvoiceProductAttributeDiscountAmountError,
  CannotInvoiceProductDiscountAmountError,
  CannotInvoiceSpecialOrderPartsError,
  CannotInvoiceStatusError,
  CannotInvoiceTotalProductDiscountAmountError,
} from 'shared/helpers/error-helper';
import { CreatedOrder, CreatedOrderItem, DropdownModel, LocalOrderInput, LocalOrderType } from 'shared/models';
import { AnalyticsService } from 'shared/services/analytics.service';
import { getCaseStatusRecord } from 'shared/utils';
import { getError } from './error-helper';
import { validateMetalAlloyAttributes, validateOrderItemsSpecialOrderParts } from './invoice/invoice.helper';
import { getShippingCharge } from './order-detail/order-detail.helper';

/**
 * destructs given column-sortOrder string to column and sortOrder
 *
 * @param sortableColumn - destructed column id and sort order
 */
export const getSortableOrder = (sortableColumn: string) => {
  const order: 'asc' | 'desc' = /^-.*/.test(sortableColumn) ? 'desc' : 'asc';
  return {
    id: sortableColumn.replace(/^-/, ''),
    order: order,
  };
};

/**
 * formats a given address object into user-friendly address
 *
 * @param address - user-friendly format
 */
export const getFormattedAddress = (address?: Address) => {
  if (!address) return '';

  let street = address.street1;
  if (address.street2) {
    street += ` ${address.street2}`;
  }
  if (street.trim().length) {
    return `${street}, ${address.city}, ${address.state ? `${address.state} ` : ''}${address.zipcode}`;
  }
  return '';
};

export const getShippingAddressOption = ({
  address,
  ...rest
}: {
  address: Address;
  secondaryLabel: string;
  secondaryLabelClassColor?: string;
  defaultColorClass?: string;
}): DropdownModel => {
  return {
    primaryLabel: getFormattedAddress(address),
    value: getFormattedAddress(address),
    ...rest,
  };
};

export const getFormattedOrderAddress = (shippingAddress: AddressInput) => {
  if (!shippingAddress) return '';
  return getFormattedAddress({
    ...shippingAddress,
    __typename: 'Address',
  });
};

/**
 *
 *
 * @param shippingCharges -
 * @param packageType -
 */
export const getSelectedShippingCharge = (
  shippingCharges: Shipping['shippingCharges'],
  packageType: Shipping['packageType']
) => {
  const charges = shippingCharges || [];
  const outboundShippingCharge = charges.find(charge => charge?.type === ShippingChargeType.Outbound);
  const isSignatureRequired = charges.some(charge => charge?.service === ShippingService.SIGNATURE_REQUIRED) || false;
  const isSaturdayDelivery = charges.some(charge => charge?.service === ShippingService.SATURDAY_DELIVERY) || false;

  return {
    carrier: outboundShippingCharge?.carrier || '',
    shippingService: outboundShippingCharge?.service || '',
    packageType: packageType || null,
    isSignatureRequired: isSignatureRequired,
    isSaturdayDelivery: isSaturdayDelivery,
    totalAmount: outboundShippingCharge?.totalAmount,
  };
};

/**
 * validates a case detail before invoicing
 *
 * @param caseDetail - case detail to be validated
 * @param account - invoice account to check if credit is on-hold
 */
export const validateCaseDetailForInvoicing = (
  caseDetail: CreatedOrder | undefined,
  account: InvoiceAccount | null
) => {
  if (!caseDetail) throw getError('Missing case details');

  let error: Error | undefined;

  const status = caseDetail.status;
  const statusReason = caseDetail.statusReason;
  const statusDisplayName = getCaseStatusRecord(status).displayName;

  if (status === OrderStatus.CallCenter) {
    error = new CannotInvoiceCallCenterError(ErrorMessage.FORWARD_CASE);
  } else if (INVALID_INVOICE_STATUSES.includes(status)) {
    error = new CannotInvoiceStatusError(`Case is ${statusDisplayName}`);
  }

  if (status === OrderStatus.Cancelled && statusReason === CancelActivity.Discard) {
    error = new CannotInvoiceCancelDiscardError(`Case is ${statusDisplayName}`);
  }

  if (!caseDetail.patientFirstName && !caseDetail.patientLastName && !caseDetail.patientId) {
    error = new CannotInvoicePatientInfoError();
  }

  if (!validateMetalAlloyAttributes(caseDetail.orderItems)) {
    error = new CannotInvoiceMissingAlloyError();
  }

  if (!account) {
    throw getError('Missing billing account info. Please contact support.');
  }

  const isAccountOnCreditHold = account.standing === AccountStanding.OnCreditHold;
  const isAccountAlwaysOnCreditHold = account.standing === AccountStanding.AlwaysOnCreditHold;

  if (isAccountOnCreditHold || isAccountAlwaysOnCreditHold) {
    error = new CannotInvoiceCreditHoldError();
  }

  const { discountError, specialOrderPartsError } = validateDiscountsAndSpecialOrderPartsForAllOrders([caseDetail]);

  if (specialOrderPartsError) {
    error = new CannotInvoiceSpecialOrderPartsError(specialOrderPartsError);
  } else if (discountError === ErrorMessage.TOTAL_DISCOUNT_AMOUNT_ERROR_MESSAGE) {
    error = new CannotInvoiceDiscountAmountError();
  } else if (discountError === ErrorMessage.TOTAL_PRODUCT_DISCOUNT_AMOUNT_ERROR_MESSAGE) {
    error = new CannotInvoiceTotalProductDiscountAmountError();
  } else if (discountError === ErrorMessage.PRODUCT_DISCOUNT_AMOUNT_ERROR_MESSAGE) {
    error = new CannotInvoiceProductDiscountAmountError();
  } else if (discountError === ErrorMessage.PRODUCT_SERVICE_ADD_ON_ATTRIBUTE_DISCOUNT_AMOUNT_ERROR_MESSAGE) {
    error = new CannotInvoiceProductAttributeDiscountAmountError();
  }

  if (error) {
    AnalyticsService.track(AnalyticsEventName.InvoiceLoadedError, {
      errorName: error.name,
      errorMessage: error.message,
    });
    throw error;
  }
};

/**
 * checks if an order pricing/tax calculation in process
 *
 * @param orderMetaData - meta-data about order
 */
export const checkPriceOrTaxBeingCalculated = (orderMetaData: Order['metadata']) => {
  const isOrderProcessing = orderMetaData?.isPricingBeingCalculated || orderMetaData?.isTaxBeingCalculated;
  return !!isOrderProcessing;
};

/**
 * checks whether given order is an RMA order
 *
 * @param order - order to check
 */
export const isRmaLocalOrder = (order: LocalOrderInput) => {
  return order.localMetadata.localOrderType === LocalOrderType.RMA;
};

/**
 * Rounds a decimal number to the specified number of decimal places.
 * @param value - The number to round.
 * @param decimalPlaces - The number of decimal places to round to. Defaults to 2.
 * @returns The rounded number.
 *
 * @example
 *
 * ```ts
 * roundDecimal(1.2345); // 1
 * roundDecimal(1.2345, 2); // 1.23
 * roundDecimal(1.2345, 3); // 1.235
 * ```
 */
export const roundDecimal = (value: number, decimalPlaces = 2) => {
  const number = round(value, decimalPlaces);
  return isNaN(number) ? 0 : number;
};

/**
 * Checks if the total discount amount of the given orders is valid.
 *
 * @param orders - The orders to check the total discount amount for.
 * @returns A boolean indicating whether the total discount amount is valid.
 */
export const checkTotalDiscountAmount = (orders: Array<CreatedOrder> | undefined) => {
  if (!orders) return false;
  // Validates discount amount, if any order has a discount amount greater than the subtotal amount.
  const isValidDiscountAmount = orders.every(item => {
    const totalDiscountAmount = item.totalDiscountAmount || 0;
    const subtotalAmount = item.subtotalAmount || 0;
    const shippingCharges = item.shipping?.shippingCharges || [];

    const inboundAmount = getShippingCharge(shippingCharges, ShippingChargeType.Inbound).amount;
    const outboundAmount = getShippingCharge(shippingCharges, ShippingChargeType.Outbound).amount;
    const finalSubTotalAmount = subtotalAmount + inboundAmount + outboundAmount;

    return totalDiscountAmount <= finalSubTotalAmount;
  });
  return isValidDiscountAmount;
};

/**
 * Checks if all products have valid discount amounts (not exceeding their total amounts).
 * @param orders - Array of orders to validate
 * @returns true if all discounts are valid, false otherwise
 */
export const checkProductDiscountAmount = (orders: Array<CreatedOrder>): boolean => {
  // Return false early if no orders provided
  if (!orders) return false;

  // Return true if all order items across all orders have valid discounts, which is less than the product price
  return orders.every(order =>
    (order.orderItems as CreatedOrderItem[]).every(
      item => (item.totalDiscountAmount || 0) <= (item.subtotalAmount || 0)
    )
  );
};

interface DiscountAndSpecialOrderPartsErrors {
  discountError: ErrorMessage | undefined;
  specialOrderPartsError: ErrorMessage | undefined;
}

/**
 * Returns validation error messages, if any exist, for the target orders.
 * @param orders - Array of orders to validate.
 * @returns validation error messages, if any exist, for the target orders.
 */
export const validateDiscountsAndSpecialOrderPartsForAllOrders = (
  orders: CreatedOrder[]
): DiscountAndSpecialOrderPartsErrors => {
  const errors: DiscountAndSpecialOrderPartsErrors = {
    discountError: undefined,
    specialOrderPartsError: undefined,
  };

  const isValidDiscountAmount = checkTotalDiscountAmount(orders);
  if (!isValidDiscountAmount) {
    errors.discountError = ErrorMessage.TOTAL_DISCOUNT_AMOUNT_ERROR_MESSAGE;
  }

  // Shows product discount amount error if product total is less than 0 (please see LMS1-8474).
  const isValidProductsDiscountAmount = checkProductDiscountAmount(orders);
  if (!isValidProductsDiscountAmount) {
    errors.discountError = ErrorMessage.TOTAL_PRODUCT_DISCOUNT_AMOUNT_ERROR_MESSAGE;
  }

  orders.forEach(invoiceCase => {
    const invoiceCaseErrorMessage = validateOrderItemsSpecialOrderParts(invoiceCase.orderItems);
    if (!errors.specialOrderPartsError && invoiceCaseErrorMessage) {
      errors.specialOrderPartsError = invoiceCaseErrorMessage;
    }

    (invoiceCase.orderItems as CreatedOrderItem[]).forEach(
      ({ addOns, services, totalDiscountAmount, unitPrice: productUnitPrice, quantity: productQuantity }) => {
        let nonProductDiscountAmount = 0;
        let nonProductTotal = 0;
        [...(addOns || []), ...(services || [])].forEach(
          ({ discountAmount, unitPrice: attributeUnitPrice, quantity: attributeQuantity }) => {
            nonProductDiscountAmount = nonProductDiscountAmount + Number(discountAmount || 0);
            const attributeTotal = (attributeUnitPrice || 0) * (attributeQuantity || 1);
            nonProductTotal = nonProductTotal + attributeTotal;
          }
        );

        if (nonProductDiscountAmount > nonProductTotal) {
          errors.discountError = ErrorMessage.PRODUCT_SERVICE_ADD_ON_ATTRIBUTE_DISCOUNT_AMOUNT_ERROR_MESSAGE;
        }

        const productDiscountAmount = (totalDiscountAmount || 0) - nonProductDiscountAmount;
        const productTotal = (productUnitPrice || 0) * (productQuantity || 1);

        if (productDiscountAmount > productTotal) {
          errors.discountError = ErrorMessage.PRODUCT_DISCOUNT_AMOUNT_ERROR_MESSAGE;
        }
      }
    );
  });

  return errors;
};

/**
 * Removes the 'id' property from each item in the array.
 * @param items - The array of items.
 * @returns A new array with the 'id' property removed from each item.
 */
export const removeIdFromItems = <T extends { id: string }>(items: Array<T>) => items.map(({ id, ...rest }) => rest);

/**
 * Check and return new origin Facility if the originFacility
 * match the OriginFacilityMapping object set on config manager
 * @param originFacility - default originFacility string
 * @returns - a display originFacility string per config manager mapping or default originFacility string
 */
export const getOriginFacility = (originFacility: string): string => {
  const originFacilityMapping = getOriginFacilityMapping();
  return originFacilityMapping[originFacility] || originFacility;
};

/**
 * Returns a message to display to the user while the pricing/tax is being calculated.
 * @param isPricingBeingCalculated - true if the order pricing is being calculated.
 * @param isTaxBeingCalculated - true if the order tax is being calculated.
 * @returns a message to display to the user while the pricing/tax is being calculated.
 */
export const getPricingMessage = (isPricingBeingCalculated: boolean, isTaxBeingCalculated: boolean): string => {
  // Only show is tax being calculated message if boolean is true and pricing is not being calculated.
  const dataBeingCalculated = !isPricingBeingCalculated && isTaxBeingCalculated ? 'Tax' : 'Pricing';
  return `Please wait. ${dataBeingCalculated} calculation in progress.`;
};
